import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { v4 as uuidv4 } from 'uuid';

export interface UploadResult {
  id: string;
  fileUrl: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  originalFilename: string;
}

export interface MediaRecord {
  id: string;
  business_card_id: string;
  block_id: string;
  file_path: string;
  file_url: string;
  file_type: string;
  file_size: number;
  mime_type: string;
  original_filename: string;
  display_order: number;
  created_at: string;
}

export interface OrphanCounts {
  orphanedFiles: number;
  unusedMedia: number;
  totalSize: number;
}

export interface OrphanInfo {
  orphanedFilePaths: string[];
  orphanedFileCount: number;
  orphanedTotalSize: number;
  unusedMediaIds: string[];
  unusedMediaPaths: string[];
  unusedMediaCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class MediaUploadService {
  private supabase = inject(SupabaseService);
  private readonly MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB

  async uploadImage(
    file: File,
    businessCardId: string,
    blockId: string,
    replaceExisting: boolean = true,
  ): Promise<UploadResult> {
    // 1. Validate file
    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error(
        `File size must be less than 3MB. Current: ${(file.size / (1024 * 1024)).toFixed(1)}MB`,
      );
    }

    if (!file.type.startsWith('image/')) {
      throw new Error('Only image files are allowed');
    }

    // 2. Get current user
    const {
      data: { user },
      error: userError,
    } = await this.supabase.getCurrentUser();
    if (userError || !user || !user.id) {
      throw new Error('User not authenticated or missing user ID');
    }

    // 3. Clean up existing media for this block if replacing
    if (replaceExisting) {
      await this.deleteMediaForBlock(businessCardId, blockId);
    }

    // 4. Generate file path
    const fileId = uuidv4();
    const fileExt = file.name.split('.').pop() || 'jpg';
    const fileName = `${fileId}.${fileExt}`;
    const filePath = `card/${businessCardId}/${user.id}/${blockId}/${fileName}`;

    try {
      // 5. Upload to storage bucket
      // Ensure we have a fresh auth session before upload
      const { data: session } = await this.supabase.client.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No valid authentication session');
      }

      const { error } = await this.supabase.client.storage
        .from('business-card-media')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;

      // 6. Get public URL
      const { data: urlData } = this.supabase.client.storage
        .from('business-card-media')
        .getPublicUrl(filePath);

      // 7. Save media record to database
      const { data: mediaRecord, error: dbError } = await this.supabase.client
        .from('media')
        .insert({
          business_card_id: businessCardId,
          block_id: blockId,
          file_path: filePath,
          file_url: urlData.publicUrl,
          file_type: 'image',
          file_size: file.size,
          mime_type: file.type,
          original_filename: file.name,
          display_order: 0,
        })
        .select()
        .single();

      if (dbError) {
        // Clean up storage if database insert fails
        await this.supabase.client.storage.from('business-card-media').remove([filePath]);
        throw dbError;
      }

      return {
        id: mediaRecord.id,
        fileUrl: urlData.publicUrl,
        filePath,
        fileSize: file.size,
        mimeType: file.type,
        originalFilename: file.name,
      };
    } catch (error) {
      // Clean up storage on any failure
      await this.supabase.client.storage.from('business-card-media').remove([filePath]);
      throw error;
    }
  }

  async deleteMedia(filePath: string): Promise<void> {
    // Delete from storage using file path
    await this.supabase.client.storage.from('business-card-media').remove([filePath]);
  }

  // Get all media for a specific block
  async getMediaForBlock(businessCardId: string, blockId: string): Promise<MediaRecord[]> {
    const { data, error } = await this.supabase.client
      .from('media')
      .select('*')
      .eq('business_card_id', businessCardId)
      .eq('block_id', blockId)
      .order('display_order', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  // Get all media for a business card
  async getMediaForBusinessCard(businessCardId: string): Promise<MediaRecord[]> {
    const { data, error } = await this.supabase.client
      .from('media')
      .select('*')
      .eq('business_card_id', businessCardId)
      .order('block_id', { ascending: true })
      .order('display_order', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  // Delete all media for a specific block
  async deleteMediaForBlock(businessCardId: string, blockId: string): Promise<void> {
    // 1. Get all media records for this block
    const mediaRecords = await this.getMediaForBlock(businessCardId, blockId);

    // 2. Delete files from storage
    if (mediaRecords.length > 0) {
      const filePaths = mediaRecords.map((record) => record.file_path);
      await this.supabase.client.storage.from('business-card-media').remove(filePaths);
    }

    // 3. Delete records from database
    const { error } = await this.supabase.client
      .from('media')
      .delete()
      .eq('business_card_id', businessCardId)
      .eq('block_id', blockId);

    if (error) throw error;
  }

  // Delete all media for a business card
  async deleteMediaForBusinessCard(businessCardId: string): Promise<void> {
    // 1. Get all media records for this business card
    const mediaRecords = await this.getMediaForBusinessCard(businessCardId);

    // 2. Delete files from storage
    if (mediaRecords.length > 0) {
      const filePaths = mediaRecords.map((record) => record.file_path);
      await this.supabase.client.storage.from('business-card-media').remove(filePaths);
    }

    // 3. Delete records from database (CASCADE will handle this automatically)
    // But we can also do it explicitly for immediate cleanup
    const { error } = await this.supabase.client
      .from('media')
      .delete()
      .eq('business_card_id', businessCardId);

    if (error) throw error;
  }

  // Delete a specific media record by ID
  async deleteMediaById(mediaId: string): Promise<void> {
    // 1. Get the media record first
    const { data: mediaRecord, error: fetchError } = await this.supabase.client
      .from('media')
      .select('file_path')
      .eq('id', mediaId)
      .single();

    if (fetchError) throw fetchError;
    if (!mediaRecord) throw new Error('Media record not found');

    // 2. Delete from storage
    await this.supabase.client.storage.from('business-card-media').remove([mediaRecord.file_path]);

    // 3. Delete from database
    const { error } = await this.supabase.client.from('media').delete().eq('id', mediaId);

    if (error) throw error;
  }
}
